// Ported from the app; keep selection rules aligned with its source.
export const emptyDeck = () => ({ ids: [], introduced: {}, seen: {}, clicked: {}, revision: '' });
export function discoveryReturnBudget(elapsedMs, repeated) {
  if (!Number.isFinite(elapsedMs) || elapsedMs < 30 * 60000) return 0;
  if (elapsedMs < 3 * 60 * 60000) return repeated ? 2 : 1;
  if (elapsedMs < 8 * 60 * 60000) return 4;
  if (elapsedMs < 24 * 60 * 60000) return 6;
  return 8;
}
export function reconcileDeck(previous, pool, revision, mode, now, returnBudget, memberIds = []) {
  const available = [...new Set(pool)];
  const members = new Set(memberIds);
  const retained = previous.ids.filter((id) => available.includes(id));
  const introduced = { ...previous.introduced };
  const removedAt = { ...previous.removed };
  const manualRotation = mode === 'manual' && revision !== previous.revision;
  const lastExit = Math.max(0, ...Object.values(removedAt));
  const lastEntry = Math.max(0, ...Object.values(introduced));
  const replacements = available
    .filter(
      (id) =>
        !retained.includes(id) &&
        (removedAt[id] === undefined ||
          now - removedAt[id] >= 30 * 60000 ||
          (manualRotation && removedAt[id] < lastExit)),
    )
    .sort(
      (a, b) =>
        (removedAt[a] ?? 0) - (removedAt[b] ?? 0) ||
        (previous.seen[a] ?? 0) - (previous.seen[b] ?? 0),
    );
  const ids = previous.ids.flatMap((old) => {
    if (available.includes(old)) return [old];
    removedAt[old] = now;
    const replacement = replacements.shift();
    if (replacement === undefined) return [];
    introduced[replacement] = now;
    return [replacement];
  });
  while (ids.length < 12 && replacements.length) {
    const id = replacements.shift();
    ids.push(id);
    introduced[id] = previous.ids.length ? now : 0;
  }
  if (
    previous.revision &&
    (revision !== previous.revision || (returnBudget ?? 0) > 0) &&
    previous.ids.length > 0
  ) {
    const eligible = ids
      .filter(
        (id) =>
          now - (introduced[id] ?? 0) >= 30 * 60000 ||
          (manualRotation &&
            ((introduced[id] ?? 0) < lastEntry ||
              ids.every((candidate) => (introduced[candidate] ?? 0) === lastEntry))),
      )
      .sort(
        (a, b) =>
          (previous.clicked[b] ?? 0) - (previous.clicked[a] ?? 0) ||
          (previous.seen[b] ?? 0) - (previous.seen[a] ?? 0),
      );
    const repeated = eligible.filter((id) => (previous.seen[id] ?? 0) >= 2);
    const count = manualRotation ? 2 : (returnBudget ?? (repeated.length >= 2 ? 2 : 1));
    // Invalidated cards already count toward the ordinary replacement budget.
    const removed = previous.ids.filter((id) => !ids.includes(id)).length;
    for (const old of eligible.slice(0, Math.max(0, count - removed))) {
      const memberSlots = ids.filter((id) => id !== old && members.has(id)).length;
      const replacementIndex = replacements.findIndex((id) => !members.has(id) || memberSlots < 6);
      if (replacementIndex < 0) continue;
      const [id] = replacements.splice(replacementIndex, 1);
      ids[ids.indexOf(old)] = id;
      introduced[id] = now;
      removedAt[old] = now;
    }
  }
  // Enforce the combined visible quota, including persisted pre-policy decks.
  // Never relabel a social card to evade the quota or invent filler candidates.
  let memberCount = 0;
  for (let index = 0; index < ids.length; index++) {
    if (!members.has(ids[index]) || ++memberCount <= 6) continue;
    const old = ids[index];
    const replacement = available.find(
      (id) =>
        !members.has(id) &&
        !ids.includes(id) &&
        (removedAt[id] === undefined ||
          now - removedAt[id] >= 30 * 60000 ||
          (manualRotation && removedAt[id] < lastExit)),
    );
    removedAt[old] = now;
    if (replacement === undefined) {
      ids.splice(index--, 1);
    } else {
      ids[index] = replacement;
      introduced[replacement] = now;
    }
  }
  // Bound on-device history while retaining all visible cards and recent exits.
  const keep = new Set([
    ...ids,
    ...Object.keys(removedAt)
      .map(Number)
      .sort((a, b) => removedAt[b] - removedAt[a])
      .slice(0, 128),
  ]);
  const bounded = (record) =>
    Object.fromEntries(Object.entries(record).filter(([id]) => keep.has(Number(id))));
  return {
    ...previous,
    ids,
    introduced: bounded(introduced),
    seen: bounded(previous.seen),
    clicked: bounded(previous.clicked),
    removed: bounded(removedAt),
    revision,
  };
}
